import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Category {
  id: string;
  name: string;
  displayName: string;
  description: string;
  type: 'image' | 'stl';
  color: string;
  icon: string;
  isActive: boolean;
  createdAt: string;
}

interface CategoryContextType {
  categories: Category[];
  getCategoriesByType: (type: 'image' | 'stl') => Category[];
  getActiveCategoriesByType: (type: 'image' | 'stl') => Category[];
  addCategory: (category: Category) => void;
  updateCategory: (id: string, category: Category) => void;
  deleteCategory: (id: string) => void;
  toggleCategoryActive: (id: string) => void;
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};

interface CategoryProviderProps {
  children: ReactNode;
}

export const CategoryProvider: React.FC<CategoryProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([
    // Image categories
    {
      id: '1',
      name: 'portfolio',
      displayName: 'Portfolio',
      description: 'Showcase images for portfolio',
      type: 'image',
      color: 'primary',
      icon: 'portfolio',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '2',
      name: 'gallery',
      displayName: 'Gallery',
      description: 'General gallery images',
      type: 'image',
      color: 'secondary',
      icon: 'gallery',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '3',
      name: 'logo',
      displayName: 'Logo',
      description: 'Logo variations and branding',
      type: 'image',
      color: 'success',
      icon: 'logo',
      isActive: true,
      createdAt: '2024-01-15',
    },
    // STL categories
    {
      id: '4',
      name: 'home',
      displayName: 'Home Decor',
      description: 'Home decoration and furniture items',
      type: 'stl',
      color: 'primary',
      icon: 'home',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '5',
      name: 'gadgets',
      displayName: 'Gadgets',
      description: 'Electronic accessories and gadgets',
      type: 'stl',
      color: 'secondary',
      icon: 'gadgets',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '6',
      name: 'art',
      displayName: 'Art',
      description: 'Artistic and decorative pieces',
      type: 'stl',
      color: 'success',
      icon: 'art',
      isActive: true,
      createdAt: '2024-01-15',
    },
    {
      id: '7',
      name: 'tools',
      displayName: 'Tools',
      description: 'Functional tools and utilities',
      type: 'stl',
      color: 'warning',
      icon: 'tools',
      isActive: true,
      createdAt: '2024-01-15',
    },
  ]);

  const getCategoriesByType = (type: 'image' | 'stl'): Category[] => {
    return categories.filter(category => category.type === type);
  };

  const getActiveCategoriesByType = (type: 'image' | 'stl'): Category[] => {
    return categories.filter(category => category.type === type && category.isActive);
  };

  const addCategory = (category: Category) => {
    setCategories(prev => [category, ...prev]);
  };

  const updateCategory = (id: string, updatedCategory: Category) => {
    setCategories(prev => prev.map(category => 
      category.id === id ? updatedCategory : category
    ));
  };

  const deleteCategory = (id: string) => {
    setCategories(prev => prev.filter(category => category.id !== id));
  };

  const toggleCategoryActive = (id: string) => {
    setCategories(prev => prev.map(category => 
      category.id === id ? { ...category, isActive: !category.isActive } : category
    ));
  };

  const value: CategoryContextType = {
    categories,
    getCategoriesByType,
    getActiveCategoriesByType,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryActive,
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};



